import { Building2, LogOut, Menu, Moon, Sun, Monitor } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useTheme, type ThemeMode } from '@/lib/theme/ThemeProvider'
import { useCompanies } from '@/lib/api/hooks'
import { NotificationBell } from './NotificationBell'
import { hu } from '@/lib/i18n/hu'

export function TopNav({ onMobileMenuClick }: { onMobileMenuClick?: () => void }) {
  const { session, activeCompanyId, activeMembership, setActiveCompany, logout } = useAuth()
  const nav = useNavigate()
  const companies = useCompanies()
  const activeCompany = companies.data?.find((c) => c.id === activeCompanyId)

  if (!session) return null

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur px-3 sm:px-4 dark:bg-slate-900/95 dark:border-slate-800 gap-2">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {/* Burger gomb csak mobilon (a Sidebar `md:flex` mellett) */}
        {onMobileMenuClick && (
          <button
            type="button"
            onClick={onMobileMenuClick}
            className="md:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Menü"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <div className="hidden sm:grid h-8 w-8 place-items-center rounded-md border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 overflow-hidden flex-shrink-0">
          {activeCompany?.logoDataUrl ? (
            <img src={activeCompany.logoDataUrl} alt="" className="h-full w-full object-contain" />
          ) : (
            <Building2 className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          )}
        </div>
        <Select
          aria-label="Aktív cég"
          value={activeCompanyId ?? ''}
          onChange={(e) => setActiveCompany(e.target.value)}
          className="h-9 w-full max-w-[14rem] sm:w-56 text-sm min-w-0"
        >
          {session.memberships.map((m) => (
            <option key={m.companyId} value={m.companyId}>
              {m.legalName} · {hu.roles[m.role]}
            </option>
          ))}
        </Select>
        {activeMembership && (
          <span className="hidden md:inline-block text-xs text-slate-500 dark:text-slate-400">
            ({hu.roles[activeMembership.role]})
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <ThemeToggle />
        <NotificationBell />
        <div className="hidden sm:flex flex-col text-right text-xs leading-tight pr-2">
          <span className="font-medium text-slate-800 dark:text-slate-200">{session.user.fullName}</span>
          <span className="text-slate-500 dark:text-slate-400">{session.user.email}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            logout()
            nav('/login', { replace: true })
          }}
          leftIcon={<LogOut className="h-4 w-4" />}
          aria-label="Kilépés"
        >
          <span className="sr-only sm:not-sr-only">{hu.nav.logout}</span>
        </Button>
      </div>
    </header>
  )
}

function ThemeToggle() {
  const { mode, resolved, setMode } = useTheme()

  const nextOrder: ThemeMode[] = ['light', 'dark', 'system']
  const currentIdx = nextOrder.indexOf(mode)
  const next = nextOrder[(currentIdx + 1) % nextOrder.length]

  const Icon = mode === 'system' ? Monitor : resolved === 'dark' ? Moon : Sun
  const label =
    mode === 'system'
      ? `Téma: rendszerszintű (${resolved === 'dark' ? 'sötét' : 'világos'})`
      : resolved === 'dark'
        ? 'Téma: sötét'
        : 'Téma: világos'

  return (
    <button
      type="button"
      onClick={() => setMode(next)}
      title={`${label} · váltás (${next})`}
      aria-label={label}
      className="rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}
